/**
 * External Places Service
 * 
 * Integrates with Google Places API to enrich place ratings
 */

import { functions, httpsCallable } from '@/lib/functions';
import type { LocationCoordinates } from './location';
import type { PlaceRatingAggregate } from './placeRatings';

type SyncPlaceRatingsRequest = {
  placeId?: string;
  coordinates?: LocationCoordinates;
  placeName?: string;
};

type SyncPlaceRatingsResponse = {
  success: boolean;
  placeId: string;
  googlePlaceId?: string;
  rating?: number;
  userRatingCount?: number;
  merged: boolean;
};

/**
 * Sync place ratings with Google Places API
 * 
 * This function calls a Cloud Function that:
 * 1. Looks up the place in Google Places API
 * 2. Gets Google's aggregate rating and review count
 * 3. Merges it with our internal ratings
 * 4. Updates the place rating aggregate
 */
export const syncExternalRatings = async (
  placeId: string,
  coordinates: LocationCoordinates,
  placeName?: string
): Promise<PlaceRatingAggregate | null> => {
  try {
    console.log('[ExternalPlaces] Syncing ratings for place:', placeId, coordinates);

    const syncPlaceRatings = httpsCallable<SyncPlaceRatingsRequest, SyncPlaceRatingsResponse>(
      functions,
      'syncPlaceRatings'
    );

    const result = await syncPlaceRatings({
      placeId,
      coordinates,
      placeName,
    });

    const data = result.data;

    if (!data.success) {
      console.warn('[ExternalPlaces] Sync was not successful');
      return null;
    }

    // Fetch the updated aggregate
    const { getPlaceRating } = await import('./placeRatings');
    const updatedAggregate = await getPlaceRating(placeId);

    if (updatedAggregate) {
      console.log('[ExternalPlaces] Successfully synced ratings:', {
        placeId: data.placeId,
        merged: data.merged,
        rating: data.rating,
        userRatingCount: data.userRatingCount,
      });
    }

    return updatedAggregate;
  } catch (error) {
    console.error('[ExternalPlaces] Error syncing external ratings:', error);
    return null;
  }
};

/**
 * Sync ratings for multiple places (batch operation)
 */
export const syncMultiplePlaceRatings = async (
  places: Array<{
    placeId: string;
    coordinates: LocationCoordinates;
    placeName?: string;
  }>
): Promise<Map<string, PlaceRatingAggregate | null>> => {
  const results = new Map<string, PlaceRatingAggregate | null>();

  // Sync places in parallel (with rate limiting)
  const batchSize = 5;
  for (let i = 0; i < places.length; i += batchSize) {
    const batch = places.slice(i, i + batchSize);
    const batchResults = await Promise.allSettled(
      batch.map((place) =>
        syncExternalRatings(place.placeId, place.coordinates, place.placeName)
      )
    );

    batch.forEach((place, index) => {
      const result = batchResults[index];
      if (result.status === 'fulfilled') {
        results.set(place.placeId, result.value);
      } else {
        console.error(
          `[ExternalPlaces] Failed to sync place ${place.placeId}:`,
          result.reason
        );
        results.set(place.placeId, null);
      }
    });

    // Rate limiting: wait between batches
    if (i + batchSize < places.length) {
      await new Promise((resolve) => setTimeout(resolve, 1000));
    }
  }

  return results;
};

